import { FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createState,
  getEnterprise,
  listStates,
} from "../../lib/api";
import { InviteForm } from "../../components/admin/InviteForm";
import { ResetPasswordForm } from "../../components/admin/ResetPasswordForm";
import { EnterpriseBrandingCard } from "../../components/admin/EnterpriseBrandingCard";
import {
  Card,
  Crumbs,
  ErrorNote,
  Loading,
  PageTitle,
} from "../../components/admin/ui";

/** Enterprise detail: its states, an add-state form, and per-state
 * "invite state head". Reachable by agrilens_super (any enterprise)
 * and the enterprise_admin (their own). */
export function AdminEnterprise() {
  const { id } = useParams();
  const enterpriseId = Number(id);
  const qc = useQueryClient();

  const entQ = useQuery({
    queryKey: ["enterprise", enterpriseId],
    queryFn: () => getEnterprise(enterpriseId),
  });
  const statesQ = useQuery({
    queryKey: ["states", enterpriseId],
    queryFn: () => listStates(enterpriseId),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["states", enterpriseId] });

  const ent = entQ.data?.enterprise;

  return (
    <div className="space-y-6">
      <Crumbs
        items={[
          { label: "Enterprises", to: "/admin/enterprises" },
          { label: ent?.name ?? `#${enterpriseId}` },
        ]}
      />
      <PageTitle
        eyebrow="Enterprise"
        title={ent?.name ?? `Enterprise #${enterpriseId}`}
        subtitle="States in this enterprise. Add states and invite their heads."
      />
      {entQ.error && <ErrorNote error={entQ.error} />}

      {ent && <EnterpriseBrandingCard enterprise={ent} />}

      <AddStateForm enterpriseId={enterpriseId} onCreated={invalidate} />

      {statesQ.isLoading && <Loading />}
      {statesQ.error && <ErrorNote error={statesQ.error} />}
      {statesQ.data && (
        <div className="space-y-3">
          {statesQ.data.states.length === 0 && (
            <div className="text-sm text-gray-500">
              No states yet — add one above.
            </div>
          )}
          {statesQ.data.states.map((s) => (
            <div
              key={s.id}
              className="bg-white rounded shadow p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <Link
                    to={`/admin/state/${s.id}`}
                    className="text-lg font-semibold text-agrilens-green-dark hover:underline"
                  >
                    {s.name}
                  </Link>
                  <span className="ml-2 text-xs font-mono text-gray-500">
                    {s.code}
                  </span>
                </div>
                <Link
                  to={`/admin/state/${s.id}`}
                  className="text-sm text-agrilens-green hover:underline"
                >
                  Manage districts →
                </Link>
              </div>
              <InviteForm
                role="state_head"
                roleLabel="state head"
                scopeType="state"
                scopeId={s.id}
              />
            </div>
          ))}
        </div>
      )}

      <ResetPasswordForm />
    </div>
  );
}

function AddStateForm({
  enterpriseId,
  onCreated,
}: {
  enterpriseId: number;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      createState({
        enterprise_id: enterpriseId,
        name: name.trim(),
        code: code.trim().toUpperCase(),
      }),
    onSuccess: () => {
      setName("");
      setCode("");
      onCreated();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <Card title="Add state">
      <form onSubmit={onSubmit} className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">State name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bihar"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="w-28">
          <label className="block text-xs text-gray-500 mb-1">Code</label>
          <input
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="BR"
            maxLength={16}
            className="w-full border rounded px-3 py-2 text-sm font-mono uppercase"
          />
        </div>
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Adding…" : "Add state"}
        </button>
      </form>
      {mut.isError && (
        <div className="mt-2">
          <ErrorNote error={mut.error} />
        </div>
      )}
    </Card>
  );
}
